﻿<script setup lang="ts">
    import { reactive, onMounted } from 'vue';
    import { uploadImage, UploadedResourceResponse, AiDropdownOption } from '@cwd/AiTools/AiEditor.access';
    import { getGenerationExample, AiImageCategory, getImageCategories, getSentencePromptExample } from '@cwd/AiTools/AiEditorExamples.access';
    import BSFormInputLabel from '@shared/components/BSFormInputLabel.vue';
    import Modal from '@shared/components/Modal.vue';
    import SelectMany from '@shared/components/SelectMany/SelectMany.vue';
    import { setupShared } from '@cwd/AiTools/Shared';
    import Terms from '@cwd/AiTools/Terms.vue';
    import { vPopoverInfo } from '@shared/utils/directives/popovers';
    import Loading from '@shared/components/Loading.vue';

    const { shared, disabled, acceptTerms, handleUnsuccessfulResponse, showConsumption, getFormattedConsumption, updateConsumption, makeOptionsRequest } =
        setupShared();

    const props = defineProps<{
        images: {
            show: any;
            imageElement: any;
            clickImageAfterInsert: any;
        };
        viewModel: any;
    }>();

    onMounted(async () => {
        props.images.show &&
            props.images.show.subscribe((newValue: boolean) => {
                shared.show = newValue;
                if (newValue === true) init();
            });
    });

    const state = reactive<{
        commandText: string;
        exampleCommandText: string;
        imageCategories: AiImageCategory[];
        exampleImagePrompts: AiDropdownOption[];
        selectedImageUrl: string;
        selectedModel: 'dalle-2' | 'dalle-3';
        selectedQuality: 'standard' | 'hd';
        selectedStyle: 'natural' | 'vivid';
        selectedSize: '1024x1024' | '1024x1792' | '1792x1024';
    }>({
        commandText: '',
        exampleCommandText: '',
        imageCategories: [],
        exampleImagePrompts: [],
        selectedImageUrl: '',
        selectedModel: 'dalle-3',
        selectedQuality: 'standard',
        selectedStyle: 'vivid',
        selectedSize: '1024x1024',
    });

    const commandText = (): string => {
        var text = state.commandText;

        state.imageCategories.forEach((category) => {
            category.SelectedOptions.forEach((option) => {
                text = text + ', ' + option;
            });
        });

        return text.substring(0, 999);
    };

    const canSave = (): boolean => {
        if (disabled()) return false;

        return shared.resultOptions && shared.resultOptions.length > 0 && state.selectedImageUrl != '';
    };

    const init = async () => {
        shared.results = [];
        shared.resultOptions = [];
        shared.current = null;

        await updateConsumption();

        if (state.imageCategories.length == 0) {
            state.imageCategories = getImageCategories();

            state.exampleCommandText = getSentencePromptExample();
            state.commandText = state.exampleCommandText;
        }
    };

    const clickImage = (imageUrl: string) => {
        if (state.selectedImageUrl == imageUrl) {
            state.selectedImageUrl = '';
        } else {
            state.selectedImageUrl = imageUrl;
        }
    };

    const save = async (insert: boolean) => {
        if (shared.loading) return;

        shared.loading = true;

        var fileName = 'Images/ai-generated-image-' + Date.now() + '.png';

        await uploadImage(fileName, state.selectedImageUrl)
            .then((responses: UploadedResourceResponse[]) => {
                if (insert) {
                    var response = responses[0];
                    var imageElement = props.images.imageElement().$imageEl;

                    imageInsertIntoElement(response.Url, imageElement);

                    props.images.show(false);
                }

                props.viewModel().loadAccountImages();
            })
            .catch(() => {
                handleUnsuccessfulResponse(null);
            })
            .finally(() => {
                shared.loading = false;
            });
    };

    const imageInsertIntoElement = (url: string, imageEl: any) => {
        if (imageEl === null) return;

        imageEl.attr('src', url).change();

        if (props.images.clickImageAfterInsert() == true) {
            imageEl.click();
        }
    };

    const clear = () => {
        state.commandText = '';

        state.imageCategories.forEach((category) => {
            category.SelectedOptions.splice(0, category.SelectedOptions.length);
        });
    };

    const randomizePrompt = () => {
        var generationExample = getGenerationExample();

        state.commandText = generationExample.Text;
    };

    const randomizeCategories = () => {
        var generationExample = getGenerationExample();

        state.imageCategories.forEach((category) => {
            var exampleCategory = generationExample.Categories.find((c) => c.Text == category.Text);

            category.SelectedOptions.splice(0, category.SelectedOptions.length);
            Object.assign(category.SelectedOptions, exampleCategory?.SelectedOptions ?? []);
        });
    };

    const randomlyGenerateImage = () => {
        randomizePrompt();

        randomizeCategories();

        generateImage();
    };

    const generateImage = () => {
        if (state.selectedModel == 'dalle-2') {
            makeOptionsRequest('generate-image', commandText());
        } else {
            var parameters = {
                quality: state.selectedQuality,
                style: state.selectedStyle,
                size: state.selectedSize,
            };
            makeOptionsRequest('generate-image-dalle-3', commandText(), parameters, imageGenerated);
        }
    };

    const imageGenerated = () => {
        if (shared.resultOptions != null && shared.resultOptions.length == 1) {
            state.selectedImageUrl = shared.resultOptions[0];
        }
    };
</script>

<script lang="ts"></script>

<template>
    <Modal
        :title="$Resources.AiAssistantCreateSurveyTitle"
        :beta="false"
        icon="ai"
        :show="shared.show"
        size="xl"
        @close="props.images.show(false)"
        :scrollBody="false">
        <template #content>
            <Terms :termsAccepted="shared.termsAccepted" @accept="acceptTerms()"></Terms>

            <div class="row">
                <div class="col-sm-6 col-lg-3">
                    <div class="ug-form-group-2">
                        <label>
                            {{ $Resources.AIAssistantModel }}
                            <button
                                type="button"
                                class="popover-trigger"
                                v-popover-info.right="{
                                    heading: $Resources.AIAssistantModel,
                                    body: $Resources.AIAssistantModelInfoBody,
                                    displayHtml: true,
                                }">
                                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                            </button>
                        </label>
                        <select class="form-control" v-model="state.selectedModel">
                            <option key="1" value="dalle-2">DALL-E 2</option>
                            <option key="2" value="dalle-3">DALL-E 3</option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6 col-lg-3" v-show="state.selectedModel == 'dalle-3'">
                    <div class="ug-form-group-2">
                        <label>
                            {{ $Resources.AIAssistantSize }}
                        </label>
                        <select class="form-control" v-model="state.selectedSize">
                            <option key="1" value="1024x1024">{{ $Resources.AIAssistantModelSizeSquare }} - 1024x1024</option>
                            <option key="2" value="1024x1792">{{ $Resources.AIAssistantModelSizePortrait }} - 1024x1792</option>
                            <option key="3" value="1792x1024">{{ $Resources.AIAssistantModelSizeLandscape }} - 1792x1024</option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6 col-lg-3" v-show="state.selectedModel == 'dalle-3'">
                    <div class="ug-form-group-2">
                        <label>
                            {{ $Resources.AIAssistantQuality }}
                            <button
                                type="button"
                                class="popover-trigger"
                                v-popover-info.right="{
                                    heading: $Resources.AIAssistantQuality,
                                    body: $Resources.AIAssistantQualityInfoBody,
                                    displayHtml: true,
                                }">
                                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                            </button>
                        </label>
                        <select class="form-control" v-model="state.selectedQuality">
                            <option key="1" value="standard">{{ $Resources.AIAssistantModelQualityStandard }}</option>
                            <option key="2" value="hd">{{ $Resources.AIAssistantModelQualityHd }}</option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6 col-lg-3" v-show="state.selectedModel == 'dalle-3'">
                    <div class="ug-form-group-2">
                        <label>
                            {{ $Resources.AIAssistantStyle }}
                            <button
                                type="button"
                                class="popover-trigger"
                                v-popover-info.right="{
                                    heading: $Resources.AIAssistantStyle,
                                    body: $Resources.AIAssistantStyleInfoBody,
                                    displayHtml: true,
                                }">
                                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                            </button>
                        </label>
                        <select class="form-control" v-model="state.selectedStyle">
                            <option key="1" value="natural">{{ $Resources.AIAssistantModelStyleNatural }}</option>
                            <option key="2" value="vivid">{{ $Resources.AIAssistantModelStyleVivid }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="m-md-b">
                <BSFormInputLabel
                    :label="$Resources.AIAssistantPrompt"
                    :infoText="$Resources.AIAssistantPromptInfoText"
                    :id="'text-input-' + $.uid"></BSFormInputLabel>

                <textarea class="form-control" :placeholder="state.exampleCommandText" v-model="state.commandText" rows="1" maxlength="500"></textarea>
            </div>

            <h4>{{ $Resources.AIAssistantCategories }}</h4>
            <div class="category-container">
                <template v-for="category in state.imageCategories">
                    <span>{{ category.Text }}</span>
                    <SelectMany
                        class="sm-item-small"
                        mode="edit"
                        :list="category.Options"
                        :selectedItems="category.SelectedOptions"
                        selectionProp="Text"
                        displayProp="Text"
                        selectOnly="Text" />
                </template>
            </div>

            <div class="m-xs-t d-flex flex-wrap justify-content-between">
                <div class="form-group d-flex flex-wrap gap-sm">
                    <button type="button" class="btn btn-default" @click="clear()">{{ $Resources.AIAssistantClear }}</button>
                    <button type="button" class="btn btn-default" @click="randomizePrompt()" :disabled="disabled()">
                        {{ $Resources.AIAssistantRandomizePrompt }}
                    </button>
                    <button type="button" class="btn btn-default" @click="randomizeCategories()" :disabled="disabled()">
                        {{ $Resources.AIAssistantRandomizeCategories }}
                    </button>
                </div>
                <div class="form-group d-flex flex-wrap gap-sm">
                    <button type="button" class="btn btn-default" @click="randomlyGenerateImage()" :disabled="disabled()">
                        {{ $Resources.AIAssistantRandomlyGenerateImage }}
                    </button>
                    <button type="button" class="btn btn-primary" @click="generateImage()" :disabled="disabled()">
                        {{ $Resources.AIAssistantGenerateImage }}
                    </button>
                </div>
            </div>

            <div class="ai-images-container" v-show="shared.resultOptions && shared.resultOptions.length > 0">
                <div v-for="imageUrl in shared.resultOptions">
                    <img
                        class="img-responsive pointer"
                        :class="{ selected: state.selectedImageUrl == imageUrl }"
                        :src="imageUrl"
                        @click="clickImage(imageUrl)" />
                </div>
            </div>

            <div v-if="showConsumption()">
                <span>{{ getFormattedConsumption() }} % {{ $Resources.AIAssistantConsumptionPostText }}</span>
            </div>

            <Loading :show="shared.loading" :fixed="true"></Loading>
        </template>
        <template #button>
            <button type="button" class="btn btn-default" @click="save(false)" :disabled="!canSave()">
                {{ $Resources.AIAssistantSaveImage }}
            </button>
            <button type="button" class="btn btn-primary" @click="save(true)" :disabled="!canSave()">
                {{ $Resources.AIAssistantInsertImage }}
            </button>
        </template>
    </Modal>
</template>

<style lang="less">
    .ai-images-container {
        display: flex;
        flex-direction: row;
        margin-top: 20px;

        div + div {
            margin-left: 10px;
        }

        img {
            border: 5px solid transparent;

            &.selected {
                border: 5px solid var(--brand-primary-400);
            }
        }
    }

    .category-container {
        display: grid;
        position: relative;
        grid-template-columns: auto 1fr;
        grid-auto-rows: minmax(40px, auto);
        place-items: center;
        column-gap: 20px;
        margin-bottom: 20px;
    }
</style>

<style scoped lang="less"></style>
